import type { AgeBand, Group } from "@/types";

/**
 * The rules a group has, restated where a person can be told about them.
 *
 * None of these is the enforcement. The backend derives `containsMinors` on
 * every write, refuses to remove the last teacher, and revokes devices when a
 * student leaves a roster. What is here decides what the screen *says* before
 * any of that happens — which is the difference between a rule and a surprise.
 */

/**
 * Mirrors the backend's single source of truth.
 *
 * Anyone under 18 counts: the UK Children's Code applies to under-18s, and
 * `mixed` counts because a group that might contain a minor has to be treated
 * as one that does.
 */
export function deriveContainsMinors(ageBand: AgeBand): boolean {
  switch (ageBand) {
    case "u13":
    case "13-15":
    case "16-17":
    case "mixed":
      return true;
    case "adult":
      return false;
  }
}

/**
 * Whether parental consent must be on file before a lesson can start.
 *
 * **Not the same question as `deriveContainsMinors`.** A 16-year-old is a minor
 * — the UK Children's Code governs how their data is handled — and no European
 * regime requires a parent to consent on their behalf. Telling a school
 * otherwise sends somebody chasing forms nobody needs.
 *
 * Mirrors the backend exactly: COPPA stops at 13, EU member states set the
 * Art. 8 GDPR age between 13 and 16 and the UK applies 13, and Spontar takes
 * the conservative European reading of under-16. A mixed group always requires
 * consent, because its composition is unknown and the safe reading is the one
 * that does not depend on being told.
 */
export function requiresParentalConsent(ageBand: AgeBand, country: string): boolean {
  if (ageBand === "adult") return false;
  if (ageBand === "u13") return true;
  if (ageBand === "mixed") return true;
  if (country === "US") return false;
  return ageBand === "13-15";
}

/** What the age-band field should say once a band and a country are known. */
export type AgeBandConsequence = "adults" | "minorsConsent" | "minorsNoConsent";

export function ageBandConsequence(ageBand: AgeBand, country: string): AgeBandConsequence {
  if (!deriveContainsMinors(ageBand)) return "adults";
  return requiresParentalConsent(ageBand, country) ? "minorsConsent" : "minorsNoConsent";
}

export const AGE_BAND_CONSEQUENCE_KEY: Record<AgeBandConsequence, string> = {
  adults: "group.ageBandAdults",
  minorsConsent: "group.ageBandMinorsWarning",
  minorsNoConsent: "group.ageBandMinorsNoConsent",
};

export type TeacherRemoval = "ok" | "lastTeacher" | "notATeacher";

/**
 * Whether this teacher can be taken off the group.
 *
 * A group with no teacher cannot run a session — there would be nobody to host
 * it — so the last one is not removable. Saying so before the button is pressed
 * is better than a failed call, because the reason is the useful part.
 */
export function teacherRemoval(group: Group, uid: string): TeacherRemoval {
  if (!group.teacherUids.includes(uid)) return "notATeacher";
  return group.teacherUids.length <= 1 ? "lastTeacher" : "ok";
}

export interface ConsentCoverage {
  required: boolean;
  recorded: number;
  total: number;
  missing: readonly { uid: string; displayName: string }[];
  complete: boolean;
}

/**
 * How much of the roster has consent on file.
 *
 * Shown on the group rather than only in settings: this is the condition that
 * stops a lesson, and a teacher should meet it here rather than in front of a
 * class.
 */
export function consentCoverage(
  group: Group,
  status: { required: boolean; recorded: number; missing: readonly { uid: string; displayName: string }[] }
): ConsentCoverage {
  return {
    required: status.required,
    recorded: status.recorded,
    total: group.roster.length,
    missing: status.missing,
    complete: !status.required || status.missing.length === 0,
  };
}
